import { ChangeDetectorRef, Component, OnDestroy, ViewChild, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { Filter } from 'src/app/models/common/filter';
import { autocompleteValidator } from 'src/app/shared/validators/autocomplete.validator';
import { AbsenceListFiltersComponent } from '../absence-list-filters/absence-list-filters.component';

@Component({
  selector: 'app-absences',
  templateUrl: './absences.component.html',
  styleUrls: ['./absences.component.scss'],
  standalone: false
})
export class AbsencesComponent implements OnDestroy {
  private formBuilder = inject(UntypedFormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);


  @ViewChild(AbsenceListFiltersComponent) absenceListFilters!: AbsenceListFiltersComponent;

  filtersFormGroup!: UntypedFormGroup;
  areFiltersExpanded: boolean = false;
  filters: Filter[] = [];
  private currentYear = new Date().getFullYear();
  private filtersSignature = '';

  private readonly unsubscribe$ = new Subject<void>();


  constructor() {
    const yearFromPath = this.tryParseYear(this.route.snapshot.paramMap.get('year'));
    if (yearFromPath != null) {
      this.currentYear = yearFromPath;
    }

    this.buildFiltersFormGroup();
    this.syncYearWithPath();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  resetFilters() {
    this.filtersFormGroup.reset({ year: this.currentYear });
    this.absenceListFilters.setInitialSelection();
  }

  toggleFiltersPanel() {
    this.areFiltersExpanded = !this.areFiltersExpanded;
  }

  onFilterDataChange = (filters: Filter[]) => {
    const nextSignature = JSON.stringify(filters);
    if (nextSignature === this.filtersSignature) {
      return;
    }

    this.filtersSignature = nextSignature;
    this.filters = filters;
  };

  private buildFiltersFormGroup() {
    this.filtersFormGroup = this.formBuilder.group({
      firstName: [null],
      lastName: [null],
      companyId: [null],
      company: [null, [autocompleteValidator]],
      employer: [null],
      employerId: [null],
      employerObject: [null],
      employerObjectId: [null],
      pesel: [null],
      absenceTypeId: [null],
      statusId: [null],
      startDate: [null],
      endDate: [null],
      absenceSyncStatusId: [null],
      subordinates: [null],
      year: [this.currentYear],
      workingTimeDimension: [null],
      activeAgreement: [null]
    });

    this.storeFiltersValueInSessionStorage();
  }

  private storeFiltersValueInSessionStorage() {
    const absencesFilters = 'absences-filters';
    const rawFilters = sessionStorage.getItem(absencesFilters);
    const filters = rawFilters ? JSON.parse(rawFilters) : null;
    const yearFromPath = this.tryParseYear(this.route.snapshot.paramMap.get('year'));

    if (filters && Object.values(filters).some((v) => !!v)) {
      const savedYear = this.tryParseYear(String(filters.year ?? ''));
      const resolvedYear = yearFromPath ?? savedYear ?? this.currentYear;
      this.currentYear = resolvedYear;

      this.filtersFormGroup.patchValue({
        ...filters,
        year: resolvedYear,
      });
      this.areFiltersExpanded = true;
    } else if (yearFromPath != null) {
      this.currentYear = yearFromPath;
      this.filtersFormGroup.patchValue({ year: yearFromPath }, { emitEvent: false });
    } else {
      this.filtersFormGroup.patchValue({ year: this.currentYear }, { emitEvent: false });
    }

    this.filtersFormGroup.valueChanges.pipe(takeUntil(this.unsubscribe$), debounceTime(1000)).subscribe(() => {
      if (this.filtersFormGroup.invalid) return;
      sessionStorage.setItem(absencesFilters, JSON.stringify(this.filtersFormGroup.getRawValue()));
    });
  }

  private syncYearWithPath() {
    this.route.paramMap.pipe(takeUntil(this.unsubscribe$)).subscribe((params) => {
      const yearFromPath = this.tryParseYear(params.get('year'));
      if (yearFromPath == null) {
        const yearFromFilter = this.tryParseYear(String(this.filtersFormGroup.get('year')?.value ?? ''));
        const effectiveYear = yearFromFilter ?? this.currentYear;
        this.currentYear = effectiveYear;
        this.filtersFormGroup.patchValue({ year: effectiveYear }, { emitEvent: false });
        return;
      }

      if (yearFromPath !== this.currentYear) {
        this.currentYear = yearFromPath;
        this.filtersFormGroup.patchValue({ year: yearFromPath }, { emitEvent: false });
      }
    });

    const yearControl = this.filtersFormGroup.get('year');
    if (!yearControl) {
      return;
    }

    yearControl.valueChanges.pipe(
      takeUntil(this.unsubscribe$), debounceTime(0),
      filter(year => year !== this.currentYear),
      filter((year: number | null): year is number => year !== null && Number.isInteger(year))
    ).subscribe((year: number) => {
      this.currentYear = year;
      const currentPathYear = Number(this.route.snapshot.paramMap.get('year'));
      if (currentPathYear === year) {
        return;
      }

      this.router.navigate(['/absences', 'list', year], { replaceUrl: true });
    });
  }

  private tryParseYear(value: string | null): number | null {
    if (!value) {
      return null;
    }

    const year = Number(value);
    return Number.isInteger(year) ? year : null;
  }
}
